import { DEFAULT_APPEARANCE } from '@/theme/engine.mjs';
import { variantSummary } from "./variant-quantities.mjs";
import { rupiah } from "@/lib/format";
import receiptFontUrl from '@/assets/receipt-fonts/RobotoCondensed-Latin.woff2';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

const methodLabel = { tunai: "Tunai", transfer: "Transfer", qris: "QRIS" };
const amount = value => rupiah(value).replace(/^Rp\s*/, '');

// Use only the saved logo for this hostname, never a theme-editor preview or
// an arbitrary URL. Assets are tenant-scoped and served from our own origin.
function savedReceiptLogo() {
  try {
    const saved = JSON.parse(localStorage.getItem('masivi_appearance:v1:' + window.location.hostname));
    const asset = saved?.branding?.logo;
    return /^[a-f0-9]{32}\.png$/.test(asset) ? `/api/v1/appearance/assets/${asset}` : '';
  } catch { return ''; }
}

// Browser text weight is separate from Epson hardware heat/density settings.
export function buildReceiptHtml(tx, s = {}, cashier = "") {
  if (!tx) return '';
  const logo = savedReceiptLogo();
  const enteredWidth = Number(s.custom_paper_width ?? 80);
  const paperWidth = s.paper === 'custom' && Number.isFinite(enteredWidth) && enteredWidth >= 40 && enteredWidth <= 210
    ? enteredWidth : (String(s.paper) === '58' ? 58 : 80);
  // Retain the existing print-head safety space (58 -> 48 mm, 80 -> 72 mm).
  // Only constrain width; the driver continues to choose page/roll length.
  const printWidth = Math.round((paperWidth - (paperWidth <= 58 ? 10 : 8)) * 100) / 100;
  // A small black outline strengthens fine strokes without making every line bold.
  // This changes browser artwork only, not the printer's thermal heat setting.
  const textStroke = ({ normal: '0', dark: '0.26', extra: '0.36' })[s.receipt_ink] || '0.26';
  const date = new Date(tx.created_at);
  const dateStr = date.toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const line = '<div class="hr"></div>';
  const row = (l, r, b) => `<div class="row${b ? " b" : ""}"><span>${escapeHtml(l)}</span><span>${escapeHtml(r)}</span></div>`;
  // Share column widths across all items, including different quantity lengths.
  // Cap unusual labels/numbers so narrow paper wraps instead of clipping.
  const qtyChars = tx.items.reduce((n, it) => Math.max(n, Math.min(6, String(it.qty).length)), 1);
  const unitChars = tx.items.reduce((n, it) => Math.max(n, Math.min(8, (it.price_level || '').trim().length)), 4);
  const amountChars = tx.items.reduce((n, it) => Math.max(n, Math.min(14, amount(it.qty * it.price).length)), 1);
  const priceChars = tx.items.reduce((n, it) => Math.max(n, Math.min(14, amount(it.price).length)), 1);

  let items = "";
  tx.items.forEach((it) => {
    const lvl = (it.price_level || "").trim().toUpperCase();
    items += `<div class="it"><div class="item-main"><div class="item-name">${escapeHtml(it.name)}</div></div>` +
      `<div class="item-detail"><span class="item-calculation"><span class="item-qty">${escapeHtml(it.qty)}</span>${lvl ? ` <span class="item-unit-slot"><span class="item-unit">${escapeHtml(lvl)}</span></span>` : '<span class="item-unit-slot"></span>'} <span class="item-times">×</span> <span class="item-price">${escapeHtml(amount(it.price))}</span></span><span class="item-amount">${escapeHtml(amount(it.qty * it.price))}</span></div>`;
    if (variantSummary(it)) items += `<div class="it-variants">${escapeHtml(variantSummary(it))}</div>`;
    if (it.discount > 0) items += `<div class="row"><span>&nbsp;&nbsp;Diskon item</span><span>-${rupiah(it.discount)}</span></div>`;
    if (it.note) items += `<div class="it-note">* ${escapeHtml(it.note)}</div>`;
    items += `</div>`;
  });

  let pays = "";
  (tx.checkout_payments || tx.payments || []).forEach((p) => { pays += row(methodLabel[p.method] || p.method, rupiah(p.amount)); });

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Struk ${escapeHtml(tx.invoice_no)}</title>
    <style>
      /* Bundled font: receipt typography must not depend on fonts installed on the till. */
      @font-face { font-family: 'Roboto Condensed'; src: url('${receiptFontUrl}') format('woff2'); font-style: normal; font-weight: 100 900; font-display: block; }
      /* Driver chooses roll length. "80mm auto" is not a valid CSS page size. */
      @page { margin: 0; size: auto; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      /* Includes padding: never send a 78mm-wide document to a 72mm print head. */
      /* TES.jpeg showed first-column clipping at 2mm. Add left safety space
         inside the existing border-box; keep the right edge and font unchanged. */
      #r { width: ${printWidth}mm; max-width: 100%; margin: 0; padding: 2mm 2mm 2mm 4mm; font-family: 'Roboto Condensed', Arial, sans-serif; font-size: 12px; font-weight: 400; line-height: 1.5; font-variant-numeric: tabular-nums; font-variant-ligatures: none; font-feature-settings: 'tnum' 1, 'zero' 0; overflow-wrap: anywhere; }
      #r, #r * { color: #000 !important; -webkit-text-fill-color: #000; -webkit-text-stroke: ${textStroke}px #000; opacity: 1; text-shadow: none; }
      .c { text-align: center; }
      .b { font-weight: 700; }
      .receipt-logo { display: block; width: auto; max-width: 100%; max-height: 22mm; margin: 0 auto 6px; object-fit: contain; filter: grayscale(1) contrast(1.4); }
      .big { font-family: 'Roboto Condensed', Arial, sans-serif; font-size: 40px; font-weight: 700; line-height: 1.1; font-feature-settings: 'tnum' 1, 'zero' 0; margin-bottom: 6px; }
      .sm { font-size: inherit; }
      .hr { border-top: 1px dashed #000; margin: 6px 0; }
      .row, .total { display: flex; justify-content: space-between; gap: 8px; }
      .row { padding: 1px 0; }
      .row > span, .total > span { min-width: 0; }
      .row > span:last-child:not(:first-child), .total > span:last-child:not(:first-child) { flex-shrink: 0; max-width: 60%; text-align: right; }
      .sale-meta { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 35%); gap: 6px; margin-top: 12px; }
      .cashier { text-align: right; }
      .invoice { margin-top: 6px; }
      .item-main { display: block; }
      .item-main > * { min-width: 0; }
      .item-amount, .item-price { text-align: right; }
      /* Receipt-wide columns keep × and both price columns aligned on every row. */
      .item-detail { padding-left: min(${paperWidth <= 58 ? 2 : 3}ch, 15%); margin-top: 4px; display: grid; grid-template-columns: minmax(0, 1fr) min(40%, ${amountChars}ch); column-gap: 8px; align-items: baseline; }
      .item-detail > span { min-width: 0; max-width: 100%; }
      .item-calculation { display: grid; grid-template-columns: minmax(0, ${qtyChars + 0.6}ch) minmax(0, ${unitChars * 1.25}ch) 1ch minmax(0, ${priceChars + 0.4}ch); column-gap: 0.4ch; align-items: baseline; justify-content: start; }
      .item-calculation > span { min-width: 0; }
      .item-qty { padding-right: 0.4ch; text-align: right; }
      .it { margin: 6px 0; break-inside: avoid; letter-spacing: 0.2px; }
      .it-note { white-space: pre-wrap; }
      .total { font-weight: 700; font-size: 14px; margin-top: 2px; }
      .row.b { font-size: 14px; }
      .item-count { margin-top: 12px; }
      .footer { margin-top: 14px; }
    </style></head><body><div id="r">
      ${logo ? `<img class="receipt-logo" src="${escapeHtml(logo)}" alt="">` : ''}
      <div class="c big">${escapeHtml(s.store_name || DEFAULT_APPEARANCE.branding.name)}</div>
      ${tx.is_reprint ? '<div class="c reprint">(REPRINT)</div>' : ''}
      ${s.address ? `<div class="c sm">${escapeHtml(s.address)}</div>` : ""}
      ${s.phone ? `<div class="c sm">Telp ${escapeHtml(s.phone)}</div>` : ""}
      <div class="sale-meta"><div>Waktu Penjualan<br>${dateStr}</div><div class="cashier">Kasir<br>${escapeHtml(cashier)}</div></div>
      <div class="invoice">#${escapeHtml(tx.invoice_no)}</div>
      ${line}
      ${row("Item", "Jumlah")}
      ${line}
      ${items}
      ${line}
      ${row("Subtotal", amount(tx.subtotal))}
      ${tx.discount > 0 ? row("Diskon", "-" + rupiah(tx.discount)) : ""}
      ${(tx.previous_debts || []).map(debt => row("Piutang sebelumnya (" + debt.invoice_no.slice(-4) + ")", rupiah(debt.amount))).join("")}
      <div class="total"><span>TOTAL</span><span>${amount(tx.total + (tx.settlement_total || 0))}</span></div>
      ${tx.fee > 0 ? row("Biaya", rupiah(tx.fee)) + row("TOTAL BAYAR", rupiah(tx.grand_total), true) : ""}
      ${tx.return_credit_applied > 0 ? row("Saldo Retur", "-" + rupiah(tx.return_credit_applied)) : ""}
      ${pays}
      ${row("Bayar", rupiah(tx.paid))}
      ${row("Kembali", rupiah(tx.change))}
      ${tx.customer_name ? row("Pelanggan", tx.customer_name) : ""}
      ${tx.credit > 0 ? row("SISA PIUTANG", rupiah(tx.credit)) : ""}
      <div class="item-count">Jumlah Item: ${tx.items.length}</div>
      ${tx.note ? `<div class="sm" style="white-space:pre-wrap;overflow-wrap:anywhere">Catatan: ${escapeHtml(tx.note)}</div>${line}` : ""}
      <div class="c sm footer" style="white-space:pre-line">${escapeHtml(s.footer || "*** TERIMA KASIH ***")}</div>
    </div>
    <script>window.onload=async function(){if(document.fonts){try{await Promise.all([document.fonts.load('400 12px "Roboto Condensed"'),document.fonts.load('700 14px "Roboto Condensed"'),document.fonts.load('700 40px "Roboto Condensed"')]);await document.fonts.ready;}catch(e){}}await Promise.all(Array.from(document.images||[]).map(async function(img){try{if(img.decode)await img.decode();if(!img.naturalWidth)img.style.display="none";}catch(e){img.style.display="none";}}));setTimeout(function(){try{window.focus();window.print();}catch(e){if(window.frameElement && window.frameElement.onReceiptPrintError)window.frameElement.onReceiptPrintError();}},150);};window.onafterprint=function(){if(window.frameElement && window.frameElement.onReceiptPrintDone)window.frameElement.onReceiptPrintDone();else window.close();};</script>
    </body></html>`;

  return html;
}

export function printReceipt(tx, s = {}, cashier = "") {
  if (!tx) return false;
  return printDocument(buildReceiptHtml(tx, s, cashier));
}

// Checkout finishes after an API request, outside the browser's click gesture.
// A same-origin print frame avoids blocked popups. Silent printing still requires
// the installed Windows shortcut's --kiosk-printing flag; normal Chrome shows a dialog.
export function printReceiptAutomatically(tx, s = {}, cashier = "", onError = () => {}) {
  if (!tx) return false;
  const frame = document.createElement('iframe');
  frame.title = 'Cetak struk otomatis';
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.dataset.receiptAutoPrint = 'true';
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:360px;height:640px;border:0;';
  let timer;
  const cleanup = () => { window.clearTimeout(timer); frame.remove(); };
  frame.onReceiptPrintDone = cleanup;
  frame.onReceiptPrintError = () => { cleanup(); onError(); };
  frame.onerror = frame.onReceiptPrintError;
  try {
    frame.srcdoc = buildReceiptHtml(tx, s, cashier);
    document.body.appendChild(frame);
    // Fallback for browsers that do not dispatch afterprint (including cancelled jobs).
    timer = window.setTimeout(cleanup, 10 * 60 * 1000);
    return true;
  } catch (error) {
    cleanup();
    return false;
  }
}

// Uses the actual receipt template, with synthetic data only. No API or stock writes.
export function buildReceiptFontTestHtml(s = {}) {
  const sample = {
    invoice_no: 'TES-CETAK', created_at: new Date().toISOString(),
    items: [
      { name: 'DJARUM SUPER 12', price_level: 'BKS', qty: 2, price: 24500, discount: 0 },
      { name: 'GG FILTER 12', price_level: 'SLOP', qty: 1, price: 235000, discount: 0 },
      { name: 'AQUA 600 ML', price_level: 'DUS', qty: 2, price: 48000, discount: 0 },
      { name: 'INDOMIE GORENG', price_level: 'PCS', qty: 5, price: 3500, discount: 0 },
      { name: 'KOPI ABC SUSU', price_level: 'RCG', qty: 3, price: 18500, discount: 0 },
    ],
    subtotal: 453000, total: 453000, paid: 453000, change: 0,
    payments: [{ method: 'tunai', amount: 453000 }],
  };
  return buildReceiptHtml(sample, { ...s, footer: 'Angka: 0123456789\nTES CETAK — BUKAN TRANSAKSI' }, 'KASIR 1');
}

export function printReceiptFontTest(s = {}) {
  return printDocument(buildReceiptFontTestHtml(s));
}

function printDocument(html) {
  const w = window.open("", "_blank", "width=360,height=640");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
